import { ParamMap } from "@angular/router";
import type { FilterCount, UpcomingEncounterRef } from "../models/admissions.model";
import {
  AdmissionsService,
  WorklistDashboardFilterTag,
} from "../services/admissions.service";
import { FilterService } from "../services/filter.service";
import {
  dashboardActivityDateRangeTagLabel,
  DASHBOARD_NOTIFICATION_QP_RANGE,
  resetDashboardNotificationFilterState,
} from "./dashboard-notification-query-params.util";
import { parseNotificationDashboardRange } from "./notification-dashboard-date-range";

/** Worklist deep link from dashboard Upcoming Visits stat tiles. */
export const DASHBOARD_WORKLIST_QP_PATIENT_IDS = "patientIds";

/** Legacy alias — single id or CSV. */
export const DASHBOARD_WORKLIST_QP_PATIENT_ID = "patientId";

/** Display label for the removable worklist tag (Encounters / Prospects / Upcoming tiles). */
export const DASHBOARD_WORKLIST_QP_FILTER_LABEL = "dashboardFilterLabel";

/** Prospects widget “Referrals” → worklist with `startDate` / `endDate` only (not notification activity filters). */
export const DASHBOARD_WORKLIST_QP_PROSPECT_REFERRALS = "prospectReferrals";

/** Prospects widget “Booked” → worklist Created Date + Scheduled encounter status. */
export const DASHBOARD_WORKLIST_QP_BOOKED = "booked";

/** Prospects widget “No Show” → worklist DOS range + No Show encounter status. */
export const DASHBOARD_WORKLIST_QP_NO_SHOW = "noShow";

/** Prospects widget “Established” → worklist DOS range + established lead_status. */
export const DASHBOARD_WORKLIST_QP_ESTABLISHED = "established";

/** Encounters widget (Open / Sign Req / Unsubmitted) → worklist DOS range + encounter_status. */
export const DASHBOARD_WORKLIST_QP_ENCOUNTER_STATUS = "encounterStatus";

/** Upcoming Visits widget (Clinic / Telehealth / Procedures / Other) → worklist DOS range + visit_type. */
export const DASHBOARD_WORKLIST_QP_VISIT_TYPE = "visitType";

/** Optional dashboard Referral Source selection forwarded to Worklist source filters. */
export const DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE = "referralSource";
export const DASHBOARD_WORKLIST_QP_REFERRAL_SOURCE_LABEL = "referralSourceLabel";

/** Optional dashboard Location selection forwarded to Worklist location filters. */
export const DASHBOARD_WORKLIST_QP_LOCATION = "location";
export const DASHBOARD_WORKLIST_QP_LOCATION_LABEL = "locationLabel";

/** Optional dashboard Schedule Resource (provider) selection forwarded to Engage/Worklist resource filters. */
export const DASHBOARD_WORKLIST_QP_RESOURCE = "resource";
export const DASHBOARD_WORKLIST_QP_RESOURCE_LABEL = "resourceLabel";

/** Unique, trimmed PHI patient ids from encounter ref lists (dashboard API buckets). */
export function patientIdsFromUpcomingEncounters(
  encounters: UpcomingEncounterRef[] | undefined
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of encounters ?? []) {
    const id = String(e?.patientId ?? "").trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Parse `patientIds` / `patientId` query values (comma-separated). */
export function parseDashboardWorklistPatientIdsQuery(raw: string | null | undefined): string[] {
  const text = (raw ?? "").trim();
  if (!text) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of text.split(",")) {
    const id = part.trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Dashboard Upcoming Visits tile → `/worklist?patientIds=…`: directory GET uses `patientId` (CSV).
 * Returns whether a non-empty filter was applied.
 */
function buildPatientScopeDashboardFilterTag(
  qp: ParamMap
): WorklistDashboardFilterTag | null {
  const label = (qp.get(DASHBOARD_WORKLIST_QP_FILTER_LABEL) ?? "").trim();
  if (!label) {
    return null;
  }
  return { id: "patientScope", label };
}

function buildPatientScopeDashboardFilterTags(
  qp: ParamMap
): WorklistDashboardFilterTag[] {
  const tags: WorklistDashboardFilterTag[] = [];
  const scopeTag = buildPatientScopeDashboardFilterTag(qp);
  if (scopeTag) {
    tags.push(scopeTag);
  }
  const rawRange = qp.get(DASHBOARD_NOTIFICATION_QP_RANGE);
  const range = parseNotificationDashboardRange(rawRange);
  if (range != null && range !== "all") {
    tags.push({
      id: "dateRange",
      label: dashboardActivityDateRangeTagLabel(qp, range),
    });
  }
  return tags;
}

export function applyDashboardWorklistPatientIdsFromQueryParams(
  qp: ParamMap,
  filterService: FilterService,
  admissions?: AdmissionsService
): boolean {
  const ids = parseDashboardWorklistPatientIdsQuery(
    qp.get(DASHBOARD_WORKLIST_QP_PATIENT_IDS) ?? qp.get(DASHBOARD_WORKLIST_QP_PATIENT_ID)
  );
  if (!ids.length) {
    return false;
  }
  // Replacing a prior Notifications deep link (e.g. No Show after Unread Messages).
  resetDashboardNotificationFilterState(filterService, admissions);
  filterService.setWorklistEngagePatientIdFilter(ids.join(","));
  if (admissions) {
    admissions.worksheetCreatedOnIso = null;
    admissions.worksheetCreatedOnEndIso = null;
    admissions.worksheetActivityDateStartIso = null;
    admissions.worksheetActivityDateEndIso = null;
    admissions.worklistDashboardFilterTags = buildPatientScopeDashboardFilterTags(qp);
    admissions.notifyWorklistDashboardFilterTagsChanged();
  }
  return true;
}

export function clearDashboardWorklistPatientIdsFilter(filterService: FilterService): void {
  filterService.setWorklistEngagePatientIdFilter(null);
}

/** Engage `GET …/dashboard/prospect` → unique trimmed ids from `total_leads_list`. */
export function patientIdsFromProspectTotalLeadsList(list: unknown): string[] {
  if (!Array.isArray(list)) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of list) {
    const id = String(x ?? "").trim();
    if (!id || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Merge `encounters` from referrals/counts filter rows matching any of `labels`. */
export function encountersFromReferralsCountFilters(
  filters: FilterCount[] | undefined,
  labels: string[]
): UpcomingEncounterRef[] {
  const out: UpcomingEncounterRef[] = [];
  const labelSet = new Set(labels.map((l) => l.trim()).filter(Boolean));
  for (const row of filters ?? []) {
    if (!labelSet.has((row.label ?? "").trim())) {
      continue;
    }
    if (Array.isArray(row.encounters)) {
      out.push(...row.encounters);
    }
  }
  return out;
}
